import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, Loader2, Plus, SlidersHorizontal, Copy, IdCard } from 'lucide-react';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
// One more seat's price, read from this practice's effective plan (platform
// pricing + any negotiated deal) — never a hardcoded number.
const seatPrice = (inv, k) => (inv?.overage_prices && inv.included_seats?.[k] !== undefined
  ? ` (${fmt(inv.overage_prices[k])}/mo beyond ${inv.included_seats[k]} included)` : '');
const roleBadge = { doctor: 'bg-primary/10 text-primary border-primary/30', admin: '', nurse: '' };

function PermissionsDialog({ user, keys, onSaved }) {
  const [open, setOpen] = useState(false);
  const [perms, setPerms] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setPerms({ ...(user.effective_permissions || {}) }); }, [open, user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/owner/users/${user.id}`, { permissions: perms });
      toast.success(`Permissions updated for ${user.name}`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(errText(e, 'Could not save permissions'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`owner-perms-open-${user.id}`}><SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Permissions</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="owner-perms-dialog">
        <DialogHeader>
          <DialogTitle>Permissions — {user.name}</DialogTitle>
          <DialogDescription>Fine-tune what this {user.role} can do. Toggles override their role defaults.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5 max-h-[50vh] overflow-y-auto pr-1">
          {Object.entries(keys || {}).map(([k, label]) => (
            <div key={k} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
              <span className="text-sm">{label}</span>
              <Switch checked={!!perms[k]} onCheckedChange={(v) => setPerms((p) => ({ ...p, [k]: v }))} data-testid={`perm-toggle-${k}`} />
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={busy} data-testid="owner-perms-save">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save permissions</Button>
      </DialogContent>
    </Dialog>
  );
}

// What a doctor's signed packets print under the signature. Owner-created
// doctors never pass through invite-accept, so this is the only place they get set.
const DOCTOR_CREDENTIALS = [['specialty', 'Specialty', 'Family Medicine'], ['npi', 'NPI', '10 digits'], ['license_number', 'License #', 'MD-123456']];

function CredentialFields({ form, setForm, testPrefix }) {
  return DOCTOR_CREDENTIALS.map(([k, label, ph]) => (
    <div key={k} className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={form[k] || ''} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} maxLength={120} data-testid={`${testPrefix}-${k}`} />
    </div>
  ));
}

function CredentialsDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ specialty: user.specialty || '', npi: user.npi || '', license_number: user.license_number || '' }); }, [open, user]);

  const save = async () => {
    // Only the keys that changed, so the audit row names what moved.
    const patch = Object.fromEntries(DOCTOR_CREDENTIALS.map(([k]) => k)
      .filter((k) => (form[k] || '').trim() !== (user[k] || '')).map((k) => [k, form[k].trim()]));
    if (!Object.keys(patch).length) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.patch(`/owner/users/${user.id}`, patch);
      toast.success(`Credentials updated for ${user.name}`);
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(errText(e, 'Could not save credentials')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`owner-creds-open-${user.id}`}><IdCard className="h-3.5 w-3.5 mr-1" /> Credentials</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Credentials — {user.name}</DialogTitle>
          <DialogDescription>Printed on every packet this doctor signs from now on. Packets already signed are not changed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3"><CredentialFields form={form} setForm={setForm} testPrefix="owner-creds" /></div>
        <Button onClick={save} disabled={busy} data-testid="owner-creds-save">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save credentials</Button>
      </DialogContent>
    </Dialog>
  );
}

function CreateUserDialog({ pid, invoice, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'nurse' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      // Credentials only travel with a doctor; a role switched away from doctor
      // must not carry what was typed before it.
      const body = form.role === 'doctor' ? form
        : Object.fromEntries(Object.entries(form).filter(([k]) => !DOCTOR_CREDENTIALS.some(([c]) => c === k)));
      const r = await api.post(`/owner/practices/${pid}/users`, body);
      setResult(r.data);
      if (r.data.overage_notice) toast.warning(r.data.overage_notice);
      onCreated?.();
    } catch (e) {
      toast.error(errText(e, 'Could not create user'));
    } finally {
      setBusy(false);
    }
  };

  const link = result ? `${window.location.origin}${result.setup_link_path}` : '';

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResult(null); setForm({ name: '', email: '', role: 'nurse' }); } }}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="owner-create-user-open"><Plus className="h-4 w-4 mr-1" /> Create user</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="owner-create-user-dialog">
        <DialogHeader>
          <DialogTitle>Create a user</DialogTitle>
          <DialogDescription>They receive a one-time link to set their own password. Seats beyond the plan are billed automatically.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="owner-create-user-result">
            <p className="text-sm">Account created. Share this one-time setup link:</p>
            <div className="flex gap-2">
              <Input readOnly value={link} className="text-xs font-mono" data-testid="owner-create-user-link" />
              <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(link); toast.success('Link copied'); }} data-testid="owner-create-user-copy"><Copy className="h-4 w-4" /></Button>
            </div>
            {result.overage_notice && <p className="text-xs text-amber-600">{result.overage_notice}</p>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="owner-create-user-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="owner-create-user-email" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="owner-create-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nurse">Medical staff{seatPrice(invoice, 'staff')}</SelectItem>
                  <SelectItem value="admin">Admin{seatPrice(invoice, 'admin')}</SelectItem>
                  <SelectItem value="doctor">Doctor{seatPrice(invoice, 'doctor')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === 'doctor' && <CredentialFields form={form} setForm={setForm} testPrefix="owner-create-user" />}
            <Button onClick={submit} disabled={busy || !form.name || !form.email} className="w-full" data-testid="owner-create-user-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create & get setup link
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function OwnerPracticeDetail() {
  const { pid } = useParams();
  const [d, setD] = useState(null);
  const [feeInput, setFeeInput] = useState('');
  const [modeInput, setModeInput] = useState('1');
  const [shareInput, setShareInput] = useState('');
  const [feeBusy, setFeeBusy] = useState(false);
  const [taxInput, setTaxInput] = useState('');

  const load = useCallback(() => {
    api.get(`/owner/practices/${pid}`).then((r) => {
      setD(r.data);
      setFeeInput(String((r.data.practice?.settings || {}).launch_price_per_doc ?? 45));
      setModeInput(String(r.data.billing?.mode ?? 1));
      setShareInput(String((r.data.practice?.settings || {}).platform_share ?? ''));
      // Stored as a fraction (0.0825); edited as a percent. Blank = not recorded.
      const rate = (r.data.practice?.settings || {}).plan_c_tax_rate;
      setTaxInput(rate === null || rate === undefined ? '' : String(Number((rate * 100).toFixed(4))));
    }).catch(() => {});
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;

  const { practice: p, users, invoice, permission_keys: keys } = d;

  const togglePractice = async (on) => {
    try {
      await api.patch(`/owner/practices/${pid}`, { disabled: !on });
      toast.success(!on ? 'Practice suspended' : 'Practice re-activated');
      load();
    } catch (e) { toast.error(errText(e, 'Update failed')); }
  };

  const setOrganisation = async (v) => {
    try {
      await api.patch(`/owner/practices/${pid}`, { organisation_id: v });
      toast.success(`${p.name} moved into ${(d.organisations || []).find((o) => o.id === v)?.name || 'the organisation'}`);
      load();
    } catch (e) { toast.error(errText(e, 'Could not move this site')); }
  };

  const setSubStatus = async (v) => {
    try { await api.patch(`/owner/practices/${pid}`, { subscription_status: v }); toast.success('Subscription status updated'); load(); }
    catch (e) { toast.error(errText(e, 'Update failed')); }
  };

  // Base plan + seats: billed, or waived for this practice. Per-document fees
  // are unaffected either way (subscriptions.saas_exempt).
  const setSaasBilling = async (v) => {
    try {
      await api.patch(`/owner/practices/${pid}`, { saas_billing: v });
      toast.success(v === 'exempt' ? `${p.name} is exempt from the base plan and seats` : `${p.name} is billed the base plan and seats`);
      load();
    } catch (e) { toast.error(errText(e, 'Update failed')); }
  };

  // LB-BILL-8: this SITE's combined state+local rate. A blank clears it, which
  // blocks a taxable Plan C invoice rather than defaulting to the state cap.
  const saveTaxRate = async () => {
    const raw = taxInput.trim();
    const pct = raw === '' ? null : parseFloat(raw);
    if (pct !== null && Number.isNaN(pct)) { toast.error('Enter the rate as a percent, e.g. 8.25'); return; }
    try {
      await api.patch(`/owner/practices/${pid}`, { plan_c_tax_rate: pct === null ? '' : Number((pct / 100).toFixed(6)) });
      toast.success(pct === null ? 'Sales-tax rate cleared' : `Sales-tax rate set to ${pct}%`);
      load();
    } catch (e) { toast.error(errText(e, 'Could not save the rate')); }
  };

  const toggleUser = async (u, on) => {
    try {
      await api.patch(`/owner/users/${u.id}`, { disabled: !on });
      toast.success(!on ? `${u.name} switched off — seat freed` : `${u.name} switched on`);
      load();
    } catch (e) { toast.error(errText(e, 'Update failed')); }
  };

  const resetLink = async (u) => {
    try {
      const r = await api.post(`/owner/users/${u.id}/reset-link`);
      await navigator.clipboard.writeText(`${window.location.origin}${r.data.link_path}`);
      toast.success('Password reset link copied to clipboard');
    } catch (e) { toast.error(errText(e, 'Could not create reset link')); }
  };

  const currentFee = Number((p.settings || {}).launch_price_per_doc ?? 45);
  const isPracticeCollects = modeInput === '3';
  // ADR 0004: this site's organisation is on Plan C, so none of ADR 0001's
  // money vocabulary applies to it — the server refuses to store it, and the
  // card below says why instead of offering a control that cannot save.
  const planC = d.billing?.pricing_model === 'planc';
  const billingChanged = (
    Number(modeInput) !== (d.billing?.mode ?? 1)
    || (!isPracticeCollects && parseFloat(feeInput) !== currentFee)
    || (isPracticeCollects && parseFloat(shareInput) !== Number((p.settings || {}).platform_share ?? NaN))
  );

  // One save for the whole money config: mode decides which of the two amounts
  // matters, so sending both would let a stale field overwrite a good value.
  const saveBilling = async () => {
    const mode = Number(modeInput);
    const body = { billing_mode: mode };
    if (mode === 3) {
      const share = parseFloat(shareInput);
      if (Number.isNaN(share) || share < 0.01 || share > 500) {
        toast.error('Enter what we invoice the practice per document ($0.01–$500)');
        return;
      }
      body.platform_share = share;
    } else {
      const fee = parseFloat(feeInput);
      // Stripe cannot charge under $0.50 — $0 would leave patients with a dead checkout.
      if (Number.isNaN(fee) || fee < 0.5 || fee > 500) {
        toast.error('Patient fee must be between $0.50 and $500');
        return;
      }
      body.launch_price_per_doc = fee;
    }
    setFeeBusy(true);
    try {
      await api.patch(`/owner/practices/${pid}`, body);
      toast.success(mode === 3
        ? `${p.name}: we now invoice $${body.platform_share.toFixed(2)} per document monthly`
        : `Patient fee for ${p.name} set to $${body.launch_price_per_doc.toFixed(2)} per document`);
      load();
    } catch (e) { toast.error(errText(e, 'Could not update billing')); }
    finally { setFeeBusy(false); }
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-5" data-testid="owner-practice-detail">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon" data-testid="owner-detail-back"><Link to="/owner/practices"><ArrowLeft className="h-4 w-4" /></Link></Button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{p.name}</h1>
            <p className="text-xs text-muted-foreground">{p.address || 'No address on file'} · {d.submissions_total} submissions</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={(p.settings || {}).saas_billing || 'auto'} onValueChange={setSaasBilling}>
            <SelectTrigger className="w-44 h-9" data-testid="owner-detail-saas-billing"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Bill base + seats</SelectItem>
              <SelectItem value="exempt">Exempt from base + seats</SelectItem>
            </SelectContent>
          </Select>
          <Select value={p.subscription_status || 'trial'} onValueChange={setSubStatus}>
            <SelectTrigger className="w-32 h-9" data-testid="owner-detail-substatus"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['trial', 'active', 'past_due', 'cancelled'].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{p.disabled ? 'Suspended' : 'Active'}</span>
            <Switch checked={!p.disabled} onCheckedChange={togglePractice} data-testid="owner-detail-practice-toggle" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="owner-detail-invoice">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Monthly subscription — {invoice.period}</CardTitle>
            <CardDescription className="text-xs">Computed live from active seats. Collection is simulated until Stripe subscriptions go live.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {invoice.lines.map((l) => (
              <div key={l.key} className="flex justify-between text-sm gap-3">
                <span className="text-muted-foreground">{l.label}{l.qty > 1 ? ` ×${l.qty}` : ''}</span>
                <span className="tabular-nums">{fmt(l.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
              <span>Total / month</span><span className="tabular-nums" data-testid="owner-detail-invoice-total">{fmt(invoice.total_monthly)}</span>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="owner-detail-seats">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Seat usage</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {[['doctor', 'Doctor'], ['admin', 'Admins'], ['staff', 'Medical staff']].map(([k, label]) => {
              const used = invoice.seat_usage[k];
              const inc = invoice.included_seats[k];
              const over = Math.max(0, used - inc);
              return (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="tabular-nums">
                    {used} / {inc} included
                    {over > 0 && <Badge variant="outline" className="ml-2 text-[10px] text-amber-600 border-amber-500/40">+{over} billed</Badge>}
                  </span>
                </div>
              );
            })}
            <p className="text-[11px] text-muted-foreground pt-1">
              {['doctor', 'admin', 'staff'].map((k) => `Extra ${k} ${fmt(invoice.overage_prices?.[k])}/mo`).join(' · ')} · Disabled users free their seat.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Which commercial customer this SITE belongs to (ADR 0003 blocker 1).
          Moving it changes whose submission ladder its volume counts towards. */}
      <Card data-testid="owner-detail-organisation">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Organisation</CardTitle>
          <CardDescription className="text-xs">
            This practice is one <span className="font-medium">site</span>. Plan C prices the organisation above it and
            assesses its submission ladder on the org-wide total, so grouping a multi-site customer here is what stops
            them being overcharged. Not yet used for live billing.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Select value={d.organisation?.id || ''} onValueChange={setOrganisation}>
            <SelectTrigger className="w-72 h-9" data-testid="owner-detail-organisation-select">
              <SelectValue placeholder="Not in an organisation yet" />
            </SelectTrigger>
            <SelectContent>
              {(d.organisations || []).map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button asChild variant="outline" size="sm"><Link to="/owner/organisations">Manage organisations</Link></Button>
          <div className="flex items-center gap-1.5 basis-full sm:basis-auto sm:ml-auto">
            <span className="text-xs text-muted-foreground shrink-0">Site sales-tax rate</span>
            <Input
              type="number" min="0" step="0.01" placeholder="not set"
              value={taxInput} onChange={(e) => setTaxInput(e.target.value)}
              className="h-9 w-24 text-right tabular-nums" data-testid="owner-detail-tax-rate"
            />
            <span className="text-sm text-muted-foreground">%</span>
            <Button size="sm" variant="outline" onClick={saveTaxRate} data-testid="owner-detail-tax-rate-save">Save</Button>
          </div>
          <p className="text-[11px] text-muted-foreground basis-full">
            The site's combined state + local rate, used on Plan C invoices. Until it is set, a taxable invoice for
            this organisation will not issue (the state maximum over-charges most addresses).
          </p>
        </CardContent>
      </Card>

      {/* Per-document billing — ADR 0001. Mode decides who charges the patient;
          platform_share is the fixed DOLLAR amount we keep per submission. */}
      <Card data-testid="owner-detail-billing-mode">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{planC ? 'Submissions' : 'Per-document billing'}</CardTitle>
          <CardDescription className="text-xs">
            {planC
              ? 'This site is in a Plan C organisation. We collect nothing from its patients, so there is no patient fee and no per-document share here.'
              : 'Owner-controlled platform pricing — practices see it read-only. Mode 2 (we collect and split) needs Stripe Connect and a state-law sign-off, so it is not selectable yet.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {planC ? (
            <p className="text-xs text-muted-foreground max-w-xl">
              Whether this practice charges its own patients, and how much, is theirs to decide and theirs to
              keep — we never take a percentage. What we invoice its organisation is the site subscription,
              seats and each verified submission. Change the model on the{' '}
              <Link to="/owner/organisations" className="underline">organisations page</Link>.
            </p>
          ) : (<>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Who charges the patient</p>
              <Select value={String(modeInput)} onValueChange={setModeInput}>
                <SelectTrigger className="h-9" data-testid="owner-billing-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(d.billing_modes || []).map((m) => (
                    <SelectItem key={m.mode} value={String(m.mode)} disabled={m.mode === 2}>
                      {m.mode}. {m.label}{m.mode === 2 ? ' — not available yet' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                Patient fee per document {isPracticeCollects && <span className="text-muted-foreground/70">(not used in mode 3)</span>}
              </p>
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  type="number" min="0.5" max="500" step="1"
                  value={feeInput}
                  onChange={(e) => setFeeInput(e.target.value)}
                  disabled={isPracticeCollects}
                  className="h-9 text-right tabular-nums"
                  data-testid="owner-fee-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                {isPracticeCollects ? 'We invoice the practice, per document' : 'We keep, per document'}
              </p>
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  type="number" min="0.01" max="500" step="1"
                  value={isPracticeCollects ? shareInput : feeInput}
                  onChange={(e) => setShareInput(e.target.value)}
                  disabled={!isPracticeCollects}
                  className="h-9 text-right tabular-nums"
                  data-testid="owner-platform-share-input"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 justify-between pt-1 border-t">
            <p className="text-[11px] text-muted-foreground pt-2 max-w-xl">
              {isPracticeCollects
                ? `The practice bills its patients itself; submissions still flow through us. We add ${fmt(Number(shareInput) || 0)} per document to their monthly Stripe invoice, on top of base + seats.`
                : 'The patient pays us at intake and we keep the full fee. Nothing is added to the practice’s invoice.'}
            </p>
            <Button
              size="sm" variant="outline" onClick={saveBilling}
              disabled={feeBusy || !billingChanged}
              data-testid="owner-billing-save"
            >
              {feeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save billing'}
            </Button>
          </div>
          </>)}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t" data-testid="owner-detail-documents">
            {[
              ['Submitted', d.documents?.submitted, d.documents?.period_label],
              ['In progress', d.documents?.in_progress, 'this month'],
              ['Completed', d.documents?.completed, 'this month'],
              // Plan C prices the organisation's month, not the document, so there
              // is no per-site money figure here that would not be invented.
              planC
                ? ['Charged to this site', '—', 'priced org-wide']
                : [isPracticeCollects ? 'Accruing to invoice' : 'Collected from patients',
                   fmt(isPracticeCollects ? d.documents?.accrued_total : d.documents?.platform_share_total), 'this month'],
            ].map(([label, value, hint]) => (
              <div key={label} className="pt-2">
                <p className="text-lg font-semibold tabular-nums">{value ?? 0}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-[11px] text-muted-foreground/70">{hint}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ADR 0002: what this practice's AI use cost US. A cost record beside its
          fair-use line — included in the subscription, never invoiced. */}
      {d.ai_usage && (
        <Card data-testid="owner-detail-ai-usage">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">AI cost (estimate) — {d.documents?.period_label || 'this month'}</CardTitle>
            <CardDescription className="text-xs">Included in the subscription and never invoiced. On Bedrock the dollar figure is an estimate.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span><span className={`font-semibold tabular-nums${d.ai_usage.over_allowance ? ' text-amber-600' : ''}`}>{fmt(d.ai_usage.cost_usd)}</span> <span className="text-muted-foreground">of {fmt(d.ai_usage.allowance_usd)} fair use</span></span>
              <span className="text-muted-foreground"><span className="tabular-nums text-foreground">{d.ai_usage.calls ?? 0}</span> model calls</span>
            </div>
            {(d.ai_usage.by_feature || []).length > 0 && (
              <div className="space-y-0.5">
                {d.ai_usage.by_feature.map((f) => (
                  <div key={f.feature} className="flex justify-between text-xs text-muted-foreground gap-3">
                    <span className="font-mono">{f.feature}</span>
                    <span className="tabular-nums">{f.calls} calls · {fmt(f.cost_usd)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-sm">Team ({users.length})</CardTitle>
            <CardDescription className="text-xs">Switch users on/off, adjust permissions, or issue password links.</CardDescription>
          </div>
          <CreateUserDialog pid={pid} invoice={invoice} onCreated={load} />
        </CardHeader>
        <CardContent className="px-0 pb-0">
          <Table data-testid="owner-detail-users-table">
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">Name</TableHead>
                <TableHead className="hidden sm:table-cell">Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>On</TableHead>
                <TableHead className="text-right pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className={u.disabled ? 'opacity-50' : ''} data-testid={`owner-user-row-${u.id}`}>
                  <TableCell className="pl-4 font-medium">{u.name}{u.mfa_enabled && <Badge variant="secondary" className="ml-2 text-[9px]">MFA</Badge>}</TableCell>
                  <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">{u.email}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] capitalize ${roleBadge[u.role] || ''}`}>{u.role}</Badge></TableCell>
                  <TableCell><Switch checked={!u.disabled} onCheckedChange={(v) => toggleUser(u, v)} data-testid={`owner-user-toggle-${u.id}`} /></TableCell>
                  <TableCell className="text-right pr-4">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      <PermissionsDialog user={u} keys={keys} onSaved={load} />
                      {u.role === 'doctor' && <CredentialsDialog user={u} onSaved={load} />}
                      <Button variant="ghost" size="sm" onClick={() => resetLink(u)} data-testid={`owner-user-reset-${u.id}`}><KeyRound className="h-3.5 w-3.5 mr-1" /> Reset</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card data-testid="owner-detail-activity">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Recent activity</CardTitle></CardHeader>
        <CardContent className="space-y-1.5 max-h-72 overflow-y-auto">
          {(d.recent_audit || []).length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
          {(d.recent_audit || []).map((l, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-xs border-b last:border-0 py-1.5">
              <span><span className="font-medium">{l.actor_name || l.actor_role}</span> — {String(l.action || '').replaceAll('_', ' ')}</span>
              <span className="text-muted-foreground shrink-0">{(l.timestamp || '').slice(0, 16).replace('T', ' ')}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
